import { Host, h } from "@stencil/core";
import { getControlRegisterProperty } from "../../../../common/registry-properties";
import { ACTION_MENU_ITEM_EXPORT_PARTS, ACTION_MENU_ITEM_PARTS_DICTIONARY, DISABLED_CLASS } from "../../../../common/reserved-names";
import { tokenMap, updateDirectionInImageCustomVar } from "../../../../common/utils";
import { getActionMenuItemMetadata } from "../parse-model";
import { focusFirstActionMenuItem } from "../utils";
const SEPARATE_BY_SPACE_REGEX = /\s+/;
const DEFAULT_GET_IMAGE_PATH_CALLBACK = (item, iconDirection) => ({ base: iconDirection === "start" ? item.startImgSrc : item.endImgSrc });
// Parts
const ACTION_LINK = `${ACTION_MENU_ITEM_PARTS_DICTIONARY.ACTION} ${ACTION_MENU_ITEM_PARTS_DICTIONARY.LINK}`;
const ACTION_LINK_EXPANDABLE = `${ACTION_LINK} ${ACTION_MENU_ITEM_PARTS_DICTIONARY.EXPANDABLE}`;
const ACTION_BUTTON = `${ACTION_MENU_ITEM_PARTS_DICTIONARY.ACTION} ${ACTION_MENU_ITEM_PARTS_DICTIONARY.BUTTON}`;
const ACTION_BUTTON_EXPANDABLE = `${ACTION_BUTTON} ${ACTION_MENU_ITEM_PARTS_DICTIONARY.EXPANDABLE}`;
const WINDOW_ID = "window";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
export class ChActionMenu {
    constructor() {
        this.actionGroupParent = false;
        this.blockAlign = "center";
        this.caption = undefined;
        this.disabled = false;
        this.endImgSrc = undefined;
        this.endImgType = "background";
        this.expandable = false;
        this.expanded = false;
        this.getImagePathCallback = undefined;
        this.inlineAlign = "center";
        this.link = undefined;
        this.model = undefined;
        this.openOnFocus = false;
        this.parts = undefined;
        this.positionTry = undefined;
        this.shortcut = undefined;
        this.startImgSrc = undefined;
        this.startImgType = "background";
    }
    #startImage;
    #endImage;
    // Refs
    #mainAction;
    el;
    endImgSrcChanged() {
        this.#endImage = this.#computeImage("end");
    }
    getImagePathCallbackChanged() {
        this.#startImage = this.#computeImage("start");
        this.#endImage = this.#computeImage("end");
    }
    partsChanged(newParts) {
        this.#setExportparts(newParts);
    }
    startImgSrcChanged() {
        this.#startImage = this.#computeImage("start");
    }
    #computeImage = (iconDirection) => {
        if ((iconDirection === "start" && !this.startImgSrc) ||
            (iconDirection === "end" && !this.endImgSrc)) {
            return undefined;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            return undefined;
        }
        const img = getImagePathCallback(this.model, iconDirection);
        return img
            ? updateDirectionInImageCustomVar(img, iconDirection)
            : undefined;
    };
    #itemContent = () => [
        h("span", { class: "content", part: ACTION_MENU_ITEM_PARTS_DICTIONARY.CONTENT }, this.caption),
        !!this.shortcut && (h("span", { "aria-hidden": "true", part: ACTION_MENU_ITEM_PARTS_DICTIONARY.SHORTCUT }, this.shortcut))
    ];
    #actionRender = () => {
        // Classes
        const startImageClasses = this.#startImage?.classes;
        const endImageClasses = this.#endImage?.classes;
        const classes = {
            action: true,
            [DISABLED_CLASS]: this.disabled,
            [`start-img-type--${this.startImgType ?? "background"} pseudo-img--start`]: !!this.#startImage,
            [startImageClasses]: !!startImageClasses,
            [`end-img-type--${this.endImgType ?? "background"} pseudo-img--end`]: !!this.#endImage,
            [endImageClasses]: !!endImageClasses
        };
        const expandable = this.expandable;
        return this.link ? (h("a", { role: this.disabled ? "link" : undefined, "aria-controls": expandable ? WINDOW_ID : null, "aria-disabled": this.disabled ? "true" : undefined, "aria-expanded": expandable ? (!!this.expanded).toString() : null, "aria-haspopup": expandable ? "true" : null, class: classes, style: this.#endImage?.styles ?? undefined, part: tokenMap({
                [ACTION_LINK_EXPANDABLE]: expandable,
                [ACTION_LINK]: !expandable,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.EXPANDED]: expandable && this.expanded,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.COLLAPSED]: expandable && !this.expanded
            }), href: !this.disabled ? this.link.url : undefined,
            // TODO: Add unit tests for this
            rel: !this.disabled ? this.link.rel : undefined,
            // TODO: Add unit tests for this
            target: !this.disabled ? this.link.target : undefined,
            // TODO: Use a different ref due to a StencilJS bug when reassigning
            // the same variable with a different element's ref in runtime
            ref: el => (this.#mainAction = el) }, this.#itemContent())) : (h("button", { popoverTarget: WINDOW_ID, "aria-controls": expandable ? WINDOW_ID : null, "aria-expanded": expandable ? (!!this.expanded).toString() : null, "aria-haspopup": expandable ? "true" : null, class: classes, part: tokenMap({
                [ACTION_BUTTON_EXPANDABLE]: expandable,
                [ACTION_BUTTON]: !expandable,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.EXPANDED]: expandable && this.expanded,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.COLLAPSED]: expandable && !this.expanded
            }), style: this.#endImage?.styles ?? undefined, disabled: this.disabled, type: "button", ref: el => (this.#mainAction = el) }, this.#itemContent()));
    };
    #popoverRender = () => (h("ch-popover", { role: "list", id: WINDOW_ID, part: ACTION_MENU_ITEM_PARTS_DICTIONARY.WINDOW, actionById: true, actionElement: this.#mainAction, blockAlign: this.blockAlign, firstLayer: this.actionGroupParent, inlineAlign: this.inlineAlign, popover: "manual", positionTry: this.positionTry, show: true }, h("slot", null)));
    #setExportparts = (parts) => {
        // TODO: Add tests for this.
        // TODO: Should be global the Regex?
        // TODO: Test this with multiple parts
        const customParts = parts
            ? `${ACTION_MENU_ITEM_EXPORT_PARTS},${parts
                .split(SEPARATE_BY_SPACE_REGEX)
                .join(",")}`
            : ACTION_MENU_ITEM_EXPORT_PARTS;
        this.el.setAttribute("exportparts", customParts);
    };
    connectedCallback() {
        this.el.setAttribute("role", "listitem");
        this.#setExportparts(this.parts);
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??=
            getControlRegisterProperty("getImagePathCallback", "ch-action-menu-render") ?? DEFAULT_GET_IMAGE_PATH_CALLBACK;
        this.#startImage = this.#computeImage("start");
        this.#endImage = this.#computeImage("end");
    }
    componentDidRender() {
        const metadata = getActionMenuItemMetadata(this.model);
        if (this.expanded && metadata.focusFirstItemAfterExpand) {
            metadata.focusFirstItemAfterExpand = false;
            // Wait until the first item is rendered
            requestAnimationFrame(() => focusFirstActionMenuItem(this.el));
        }
        if (!this.expanded && metadata.focusAfterCollapse) {
            metadata.focusAfterCollapse = false;
            this.el.focus();
        }
    }
    render() {
        return (h(Host, { key: 'bcd3c1ad46dfd2c7e0a1626f2a21e57385d5b6ae', style: this.#startImage?.styles ?? undefined }, this.#actionRender(), this.expandable && this.expanded && this.#popoverRender()));
    }
    static get is() { return "ch-action-menu"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["action-menu.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-menu.css"]
        };
    }
    static get properties() {
        return {
            "actionGroupParent": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the current parent of the item is the action-group control."
                },
                "attribute": "action-group-parent",
                "reflect": false,
                "defaultValue": "false"
            },
            "blockAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../../../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the block alignment of the dropdown menu that is placed\nrelative to the expandable button."
                },
                "attribute": "block-align",
                "reflect": false,
                "defaultValue": "\"center\""
            },
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the caption that the control will display."
                },
                "attribute": "caption",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "endImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the end image."
                },
                "attribute": "end-img-src",
                "reflect": false
            },
            "endImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ImageRender",
                    "resolved": "\"background\" | \"img\" | \"mask\"",
                    "references": {
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the end image will be rendered."
                },
                "attribute": "end-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "expandable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the item contains a subtree. `true` if the item has a\nsubtree."
                },
                "attribute": "expandable",
                "reflect": false,
                "defaultValue": "false"
            },
            "expanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean | undefined",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display the dropdown menu."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionMenuImagePathCallback",
                    "resolved": "(item: ActionMenuItemActionableModel, iconDirection: \"start\" | \"end\") => GxImageMultiState",
                    "references": {
                        "ActionMenuImagePathCallback": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/action-menu/types.ts::ActionMenuImagePathCallback"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc or endImgSrc needs to be resolved."
                }
            },
            "inlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../../../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the dropdown menu that is placed\nrelative to the expandable button."
                },
                "attribute": "inline-align",
                "reflect": false,
                "defaultValue": "\"center\""
            },
            "link": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ItemLink | undefined",
                    "resolved": "{ url: string; rel?: string; target?: \"_self\" | \"_blank\" | \"_parent\" | \"_top\" | \"_unfencedTop\"; }",
                    "references": {
                        "ItemLink": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ItemLink"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the hyperlink properties of the item. If this property is\ndefined, the `ch-action-menu` will render an anchor tag with this\nproperties. Otherwise, it will render a button tag."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionMenuItemActionableModel",
                    "resolved": "{ id?: string; caption: string; disabled?: boolean; endImgSrc?: string; endImgType?: \"mask\" | \"background\"; expanded?: boolean; items?: ActionMenuModel; itemsBlockAlign?: ChPopoverAlign; itemsInlineAlign?: ChPopoverAlign; link?: ItemLink; parts?: string; positionTry?: \"none\" | \"flip-block\" | \"flip-inline\"; shortcut?: string; startImgSrc?: string; startImgType?: \"mask\" | \"background\"; type?: \"actionable\"; }",
                    "references": {
                        "ActionMenuItemActionableModel": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/action-menu/types.ts::ActionMenuItemActionableModel"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the extended model of the control. This property is only needed\nto know the UI Model on each event"
                }
            },
            "openOnFocus": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the dropdown menu should be opened when the expandable\nbutton of the control is focused.\nTODO: Add implementation"
                },
                "attribute": "open-on-focus",
                "reflect": false,
                "defaultValue": "false"
            },
            "parts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in every DOM element of the control."
                },
                "attribute": "parts",
                "reflect": false
            },
            "positionTry": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "resolved": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies an alternative position to try when the popover overflows the\nwindow."
                },
                "attribute": "position-try",
                "reflect": false
            },
            "shortcut": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the shortcut caption that the control will display."
                },
                "attribute": "shortcut",
                "reflect": false
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src for the left img."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ImageRender",
                    "resolved": "\"background\" | \"img\" | \"mask\"",
                    "references": {
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the start image will be rendered."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "endImgSrc",
                "methodName": "endImgSrcChanged"
            }, {
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "parts",
                "methodName": "partsChanged"
            }, {
                "propName": "startImgSrc",
                "methodName": "startImgSrcChanged"
            }];
    }
}
